import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import type { OrderEnvelope, Profile } from "@tilt/pos-sdk";

import { getPosClient } from "../lib/api";
import { POS_ORDER_CREATE, POS_PAYMENT_INITIATE } from "../lib/permissions";
import { usePageTitle } from "../lib/use-page-title";
import { useAppShell } from "../lib/useAppShell";
import { LocationPicker, type LocationOption } from "../components/sale/LocationPicker";
import { OrderDetailView } from "../components/sale/OrderDetailView";
import { SaleStepIndicator } from "../components/sale/SaleStepIndicator";
import { TakePaymentWizard } from "../components/sale/TakePaymentWizard";
import { Alert } from "../components/ui/Alert";
import { Card } from "../components/ui/Card";
import { PageSpinner } from "../components/ui/Spinner";

/**
 * SalePage - the V2 POS-1.0 payment-intake surface.
 *
 * Single page with progressive disclosure across three stages:
 *
 *   1. ``compose`` - amount + reference + customer + location.
 *      Submit opens an order on the backend and we move to stage 2.
 *
 *   2. ``collect`` - order is open; cashier picks a payment method
 *      and confirms. Each successful payment lands as a row in the
 *      timeline. As long as ``remaining > 0`` the cashier can keep
 *      taking partial payments (split tender). When ``remaining == 0``
 *      we slide into stage 3.
 *
 *   3. ``done`` - order is paid. Show the receipt panel + a "new
 *      sale" CTA.
 *
 * Permissions are checked client-side as a UX guard; the API is
 * authoritative. A user without ``pos.payment.initiate`` who somehow
 * gets here sees a friendly notice instead of the form.
 */

/**
 * Sale wizard state machine (#162 re-alignment per user 2026-04-27
 * spec). Two stages from SalePage's perspective:
 *
 *   wizard  → payment (collect/done)
 *
 * The wizard stage delegates to ``<TakePaymentWizard>`` - a self-
 * contained 3-step component (department → reference → details)
 * that owns its own state and emits ``onOrderCreated(envelope)``
 * when POST /orders succeeds. The same component will be reused
 * by #135 dealer-inventory's "take payment for this vehicle"
 * entry point with pre-filled props.
 */
interface WizardStage {
  readonly kind: "wizard";
}
interface CollectStage {
  readonly kind: "collect";
  readonly envelope: OrderEnvelope;
}
interface DoneStage {
  readonly kind: "done";
  readonly envelope: OrderEnvelope;
}

type Stage = WizardStage | CollectStage | DoneStage;

export function SalePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { openRecentPanel } = useAppShell();
  usePageTitle("sale");

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  const [stage, setStage] = useState<Stage>({ kind: "wizard" });
  const [pickedLocationId, setPickedLocationId] = useState<string | null>(null);

  // Inventory page (#135) navigates here with location.state pre-fill
  // so the wizard opens with stock # → reference and listed_price →
  // total already populated. Treated as a one-shot hand-off - once
  // the wizard mounts, navigating away and back returns to the empty
  // wizard.
  const navState = useLocation().state as {
    readonly initialReference?: string;
    readonly initialTotalCents?: number | null;
    readonly initialDepartmentId?: string | null;
    /** Resolve the initial dept by canonical name (case-insensitive).
     *  Inventory CTA passes ``"sales"`` so vehicle take-deposits land
     *  on Sales even when the cashier's per-membership default is a
     *  different dept (Parts, Service, …). Resolved against the
     *  location's department list inside the wizard. */
    readonly initialDepartmentName?: string;
    /** V0058 - Inventory CTA pre-fills the order's description with
     *  the vehicle's details so the back-office reviewer doesn't have
     *  to cross-reference inventory later. Optional - non-inventory
     *  navigators omit it and the wizard renders an empty description
     *  field. */
    readonly initialDescription?: string;
  } | null;

  // Loading / error gates.
  if (profile.isPending) {
    return <PageSpinner label={t("common.loading")} />;
  }
  if (profile.isError || !profile.data) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="error" title={t("shell.loadFailed")}>
          {t("shell.tryAgain")}
        </Alert>
      </div>
    );
  }

  // Permission gate (UX-side; API is authoritative).
  const perms = new Set(profile.data.permissions);
  if (!perms.has(POS_ORDER_CREATE) || !perms.has(POS_PAYMENT_INITIATE)) {
    return (
      <div className="max-w-md py-12">
        <Alert tone="warn" title={t("sale.noPermissionTitle")}>
          {t("sale.noPermissionBody")}
        </Alert>
      </div>
    );
  }

  // Resolve location options + active selection.
  const locationOptions = collectLocationOptions(profile.data);
  const activeLocationId = pickActiveLocationId({
    profile: profile.data,
    options: locationOptions,
    picked: pickedLocationId,
  });
  const requiresLocationPicker =
    profile.data.active_membership?.scope_kind !== "location" && locationOptions.length > 1;

  const noLocations = locationOptions.length === 0 && !activeLocationId;

  // ----- Stage: wizard (department → reference → details). Delegates
  // to <TakePaymentWizard> which owns its own internal state. ----- //
  if (stage.kind === "wizard") {
    if (!activeLocationId) {
      return (
        <WizardChrome
          step={1}
          location={{
            requiresPicker: requiresLocationPicker,
            options: locationOptions,
            value: activeLocationId,
            onChange: setPickedLocationId,
            noLocations,
          }}
          onViewTransactions={openRecentPanel}
        >
          <Alert tone="warn" title={t("sale.noLocationsTitle")}>
            {t("sale.noLocationsBody")}
          </Alert>
        </WizardChrome>
      );
    }
    return (
      <WizardChrome
        step={1}
        location={{
          requiresPicker: requiresLocationPicker,
          options: locationOptions,
          value: activeLocationId,
          onChange: setPickedLocationId,
          noLocations,
        }}
        onViewTransactions={openRecentPanel}
      >
        <TakePaymentWizard
          profile={profile.data}
          locationId={activeLocationId}
          {...(navState?.initialReference != null
            ? { initialReference: navState.initialReference }
            : {})}
          {...(navState?.initialTotalCents != null
            ? { initialTotalCents: navState.initialTotalCents }
            : {})}
          {...(navState?.initialDepartmentId != null
            ? { initialDepartmentId: navState.initialDepartmentId }
            : {})}
          {...(navState?.initialDepartmentName != null
            ? { initialDepartmentName: navState.initialDepartmentName }
            : {})}
          {...(navState?.initialDescription != null
            ? { initialDescription: navState.initialDescription }
            : {})}
          onOrderCreated={(env) => {
            void qc.invalidateQueries({ queryKey: ["orders"] });
            setStage({ kind: "collect", envelope: env });
          }}
        />
      </WizardChrome>
    );
  }

  // ----- Stage: collect / done ----- //
  // Use OrderDetailView for all post-wizard states -- it handles open,
  // partially paid, paid, in-flight, and refunded consistently, the same
  // as the Transactions detail page. No separate collect/done split needed.
  return (
    <div className="max-w-7xl space-y-6 pb-12">
      <SaleStepIndicator step="collect" hasDepartment />
      <OrderDetailView
        orderId={stage.envelope.order.id}
        listOrder={stage.envelope.order}
        onBack={() => setStage({ kind: "wizard" })}
        backLabel={t("sale.newSale")}
      />
    </div>
  );
}

// ---------------------------------------------------------------- //
// Wizard chrome - common header / location / sticky bar           //
// ---------------------------------------------------------------- //

function WizardChrome(props: {
  readonly step: 1 | 2 | 3;
  readonly location: {
    readonly requiresPicker: boolean;
    readonly options: readonly LocationOption[];
    readonly value: string | null;
    readonly onChange: (id: string) => void;
    readonly noLocations: boolean;
  };
  readonly onViewTransactions: () => void;
  readonly children: React.ReactNode;
}) {
  const { t } = useTranslation();
  // The step indicator was previously rendered both here AND inside
  // TakePaymentWizard, stacking two duplicate "Department / … / …"
  // rows on the page (regression from #162 / #135 prep). The inner
  // wizard's indicator is the authoritative one for the wizard stage
  // (it carries finer-grained sub-step labels - Reference / Customer
  // & amount - and reacts to the wizard's own state). After the
  // wizard completes the page transitions to a payment-collect view
  // that shows its own header + order summary, so a top-level
  // stepper is no longer needed there either. ``props.step`` is kept
  // on the contract so callers don't need to change but is now
  // ignored.

  return (
    <div className="max-w-7xl space-y-6 pb-20">
      <header className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">
            {t("sale.kicker")}
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-strong sm:text-3xl xl:text-4xl">
            {t("sale.title")}
          </h1>
        </div>
        <button
          type="button"
          onClick={props.onViewTransactions}
          className="hidden items-center gap-1 text-xs font-medium text-muted hover:text-strong sm:inline-flex"
        >
          <ListChecks className="h-3.5 w-3.5" />
          {t("sale.viewTransactions")}
        </button>
      </header>

      {props.location.noLocations && (
        <Alert tone="warn" title={t("sale.noLocationsTitle")}>
          {t("sale.noLocationsBody")}
        </Alert>
      )}

      {props.location.requiresPicker && (
        <Card title={t("sale.location")}>
          <LocationPicker
            options={props.location.options}
            value={props.location.value}
            onChange={props.location.onChange}
          />
        </Card>
      )}

      {props.children}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Helpers                                                           //
// ---------------------------------------------------------------- //

function collectLocationOptions(profile: Profile): LocationOption[] {
  const seen = new Map<string, LocationOption>();
  for (const m of profile.memberships) {
    if (m.scope_kind === "location" && m.location?.id) {
      seen.set(m.location.id, {
        id: m.location.id,
        name: m.location.name ?? m.location.id,
        orgName: m.organization?.name ?? null,
      });
    }
  }
  return Array.from(seen.values());
}

function pickActiveLocationId(args: {
  readonly profile: Profile;
  readonly options: readonly LocationOption[];
  readonly picked: string | null;
}): string | null {
  if (args.picked) return args.picked;
  const active = args.profile.active_membership;
  if (active?.scope_kind === "location" && active.location?.id) {
    return active.location.id;
  }
  if (args.options.length > 0) return args.options[0]!.id;
  return null;
}
